import { useState } from 'react';
import type { Asignacion } from '../../../api/ops/listas';
import type { InvitadoApuntado } from '../../../api/ops/portalRrpp';
import { ErrorApi } from '../../../api/problema';
import { Campo } from '../../../componentes/Campo';
import { Desplegable } from '../../../componentes/Desplegable';
import { OpcionRadio } from '../../../componentes/OpcionRadio';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { claveIdempotencia } from '../../../api/comun';
import { usePermisos } from '../../../permisos/permisos';
import { Contador } from '../../../componentes/Contador';
import { Bloque, Caja, Dato, Fila, TarjetaEnlace, Vacia } from '../comun';
import { codigoDe, useAvisarError } from '../uso';
import { altaDeFormulario, contactoInvitado, cuandoEs, erroresApuntar, estadoInvitado, nombreRrpp, opsDe, PERMISOS, sinProtocolo, type FormularioApuntar } from '../textos';
import type { ContextoListas } from './ApartadoListas';

/**
 * Apuntar invitado (10.9): lista, nombre e **Instagram o email** (G49, G52; nunca teléfono ni WhatsApp). Sin Instagram
 * conectado la opción sale desactivada; si aun así la API responde `409 instagram_not_connected` (G788), se pasa a email.
 * Por Instagram el resultado es su enlace personal para enviárselo (G519); por email, «pase enviado». Ya apuntado: el
 * mismo invitado (G52). La clave de idempotencia se guarda mientras no cambie lo escrito.
 */
export function Apuntar({ ctx, listaInicial, asignaciones }: { ctx: ContextoListas; listaInicial?: string; asignaciones: readonly Asignacion[] }) {
    const { props, evento, listas } = ctx;
    const { negocio, movil } = props;
    const ops = opsDe(props.ops);
    const permisos = usePermisos();
    const avisarError = useAvisarError();
    const abiertas = listas.filter((l) => l.status === 'open');
    const vacio = (lista: string): FormularioApuntar => ({ lista, nombre: '', canal: ctx.instagram === false ? 'email' : 'instagram', instagram: '', email: '', conEmail: false, acompanantes: 0, rrpp: '', respuestas: {} });
    const [f, setF] = useState<FormularioApuntar>(() => vacio(listaInicial ?? abiertas[0]?.id ?? ''));
    const [probado, setProbado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [errorApi, setErrorApi] = useState<string | null>(null);
    const [sinInstagram, setSinInstagram] = useState(ctx.instagram === false);
    const [resultado, setResultado] = useState<InvitadoApuntado | null>(null);
    const [clave, setClave] = useState(claveIdempotencia);
    const lista = abiertas.find((l) => l.id === f.lista);
    const errores = probado ? erroresApuntar(f, lista?.fields ?? []) : {};
    const cambiar = (c: Partial<FormularioApuntar>) => {
        setF({ ...f, ...c });
        setClave(claveIdempotencia());
        setErrorApi(null);
    };
    const conCupo = asignaciones.filter((a) => a.status === 'active' && a.lists.some((x) => x.guest_list_id === f.lista && x.quota !== 0));

    const enviar = () => {
        if (Object.keys(erroresApuntar(f, lista?.fields ?? [])).length > 0) {
            setProbado(true);
            return;
        }
        setOcupado(true);
        ops.addGuest(negocio, altaDeFormulario(f), clave)
            .then((r) => {
                setResultado(r);
                ctx.recargar();
            })
            .catch((e: unknown) => {
                const codigo = codigoDe(e);
                // G856: `instagram_not_connected` es el `code` del `Problem`.
                if (e instanceof ErrorApi && e.codigo === 'instagram_not_connected') {
                    setSinInstagram(true);
                    setF({ ...f, canal: 'email' });
                    setErrorApi('Instagram no está conectado: apúntalo por email.');
                } else if (codigo === 'capacity_unavailable') {
                    setErrorApi('No quedan plazas: la lista, el evento o el cupo del RRPP están llenos.');
                } else {
                    avisarError(e, 'No se ha podido apuntar. Inténtalo de nuevo.');
                }
            })
            .finally(() => { setOcupado(false); });
    };

    if (resultado !== null) {
        const g = resultado.guest;
        const ya = resultado.outcome === 'already_registered';
        const porInstagram = resultado.delivery === 'waiting_instagram_message';
        const url = resultado.personal_link?.instagram_url ?? null;
        return (
            <PanelLateral
                abierto
                onCerrar={() => { ctx.abrir(null); }}
                movil={movil}
                titulo={ya ? 'Ya estaba apuntado' : porInstagram ? 'Apuntado. Falta su enlace' : 'Apuntado y pase enviado'}
                eyebrow={`${lista?.name ?? ''} · ${evento?.name ?? ''}`}
                icono={ya ? 'user-check' : 'check'}
                accion="Apuntar a otro"
                accionIcono="user-plus"
                onAccion={() => { setResultado(null); setProbado(false); setF(vacio(f.lista)); setClave(claveIdempotencia()); }}
            >
                <div className="rv-ca-cuerpo">
                    {ya ? (
                        <>
                            <Caja icono="user-check">{g.display_name} ya está apuntado en este evento. Solo hay un pase por persona y evento.</Caja>
                            <Fila inicial titulo={g.display_name} linea={`${contactoInvitado(g)[0]} · ${estadoInvitado(g, false).texto}`} onAbrir={() => { ctx.abrir({ tipo: 'invitado', id: g.id }); }} />
                        </>
                    ) : porInstagram ? (
                        <>
                            <Caja icono="instagram" titulo="Último paso.">
                                Copia su enlace personal y envíaselo a @{f.instagram.replace(/^@/, '')} por Instagram desde tu cuenta. Cuando escriba, le llega el QR en el chat.
                            </Caja>
                            {url !== null ? (
                                <TarjetaEnlace titulo={`Enlace de ${g.display_name}`} texto="Solo vale para esta persona y este evento." chip={['Sin usar', 'pending']} filas={[{ k: 'Instagram', v: sinProtocolo(url) }]} />
                            ) : (
                                <Caja icono="instagram" tono="aviso">El enlace de Instagram sale cuando conectes la cuenta de Instagram del negocio.</Caja>
                            )}
                        </>
                    ) : (
                        <>
                            <Caja icono="mail-check" titulo="Listo.">Le hemos enviado el pase por email, con Apple Wallet y Google Wallet.</Caja>
                            <Dato icono="users" k="Plazas" v={g.companions > 0 ? `${String(1 + g.companions)} (con ${String(g.companions)} acompañante${g.companions > 1 ? 's' : ''})` : '1'} />
                        </>
                    )}
                </div>
            </PanelLateral>
        );
    }

    return (
        <PanelLateral
            abierto
            onCerrar={() => { ctx.abrir(null); }}
            movil={movil}
            titulo="Apuntar invitado"
            eyebrow={evento?.name ?? ''}
            subtitulo={cuandoEs(evento?.starts_at)}
            icono="user-plus"
            accion={abiertas.length > 0 ? 'Apuntar' : ''}
            accionIcono="user-plus"
            ocupado={ocupado}
            ocupadoTexto="Apuntando…"
            onAccion={enviar}
        >
            <div className="rv-ca-cuerpo">
                {abiertas.length === 0 ? (
                    <Vacia icono="lock" titulo="No hay ninguna lista abierta" texto="Para apuntar a alguien, abre o crea una lista en este evento." />
                ) : (
                    <>
                        <Bloque>
                            <Desplegable
                                etiqueta="Lista"
                                valor={f.lista}
                                error={errores.lista}
                                onChange={(v) => { cambiar({ lista: v, acompanantes: Math.min(f.acompanantes, abiertas.find((x) => x.id === v)?.max_companions ?? 0), rrpp: '' }); }}
                                opciones={abiertas.map((x) => ({ valor: x.id, texto: `${x.name ?? ''}${x.capacity != null ? ` · quedan ${String(Math.max(0, x.capacity - x.guests_count))}` : ''}` }))}
                            />
                            <Campo etiqueta="Nombre" valor={f.nombre} onChange={(v) => { cambiar({ nombre: v }); }} error={errores.nombre} max={120} nombre="nombre-invitado" mayusculas="words" />
                        </Bloque>
                        <Bloque titulo="Cómo le llega el pase">
                            <div className="rv-ca-dos" role="radiogroup" aria-label="Cómo le llega el pase">
                                <OpcionRadio elegida={f.canal === 'instagram'} onElegir={() => { cambiar({ canal: 'instagram' }); }} titulo="Instagram" texto={sinInstagram ? 'Conecta Instagram para usarlo' : 'Le envías su enlace desde tu cuenta'} icono="instagram" bloqueada={sinInstagram} />
                                <OpcionRadio elegida={f.canal === 'email'} onElegir={() => { cambiar({ canal: 'email' }); }} titulo="Email" texto="Le llega el pase en el acto" icono="mail" />
                            </div>
                            {f.canal === 'instagram' ? (
                                <>
                                    <Campo etiqueta="Usuario de Instagram" prefijo="@" valor={f.instagram} onChange={(v) => { cambiar({ instagram: v }); }} error={errores.instagram} max={31} nombre="instagram" mayusculas="none" />
                                    {f.conEmail ? (
                                        <Campo etiqueta="Email (opcional)" tipo="email" valor={f.email} onChange={(v) => { cambiar({ email: v }); }} error={errores.email} ayuda="El pase sale igualmente por Instagram." nombre="email-opcional" />
                                    ) : (
                                        <button type="button" className="rv-ls-mas" onClick={() => { cambiar({ conEmail: true }); }}>+ Añadir también su email (opcional)</button>
                                    )}
                                </>
                            ) : (
                                <Campo etiqueta="Email" tipo="email" valor={f.email} onChange={(v) => { cambiar({ email: v }); }} error={errores.email} nombre="email-invitado" />
                            )}
                            {errorApi !== null && <Caja icono="circle-alert" tono="error">{errorApi}</Caja>}
                            <Caja icono="ban">Las listas nunca usan WhatsApp ni piden teléfono.</Caja>
                        </Bloque>
                        <Bloque titulo="Más datos">
                            {(lista?.max_companions ?? 0) > 0 && (
                                <Contador min={0} etiqueta="Acompañantes" valor={f.acompanantes} max={lista?.max_companions ?? 0} ayuda={`Hasta ${String(lista?.max_companions ?? 0)} en esta lista. Cada uno lleva su pase.`} onCambio={(v) => { cambiar({ acompanantes: v }); }} />
                            )}
                            {permisos.puede(PERMISOS.asignar) && (
                                <Desplegable
                                    etiqueta="Apuntado por"
                                    valor={f.rrpp}
                                    onChange={(v) => { cambiar({ rrpp: v }); }}
                                    ayuda="Cuenta para su cupo y su comisión."
                                    opciones={[{ valor: '', texto: 'La casa (el equipo)' }, ...conCupo.map((a) => ({ valor: a.member_id, texto: nombreRrpp(ctx.rrpp.get(a.member_id)) }))]}
                                />
                            )}
                            {(lista?.fields ?? []).map((c) =>
                                c.kind === 'select' ? (
                                    <Desplegable
                                        key={c.label}
                                        etiqueta={`${c.label}${c.required === true ? '' : ' (opcional)'}`}
                                        valor={f.respuestas[c.label] ?? ''}
                                        error={errores[`campo:${c.label}`]}
                                        onChange={(v) => { cambiar({ respuestas: { ...f.respuestas, [c.label]: v } }); }}
                                        opciones={[{ valor: '', texto: 'Sin respuesta' }, ...(c.options ?? []).map((o) => ({ valor: o, texto: o }))]}
                                    />
                                ) : (
                                    <Campo
                                        key={c.label}
                                        etiqueta={`${c.label}${c.required === true ? '' : ' (opcional)'}`}
                                        valor={f.respuestas[c.label] ?? ''}
                                        error={errores[`campo:${c.label}`]}
                                        max={500}
                                        onChange={(v) => { cambiar({ respuestas: { ...f.respuestas, [c.label]: v } }); }}
                                    />
                                ),
                            )}
                        </Bloque>
                    </>
                )}
            </div>
        </PanelLateral>
    );
}
